import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { SyntheticEvent, memo, useCallback, useMemo } from 'react';
import { Box, Truncatable } from '@bridebook/ui';
import BookmarkFlag from '@bridebook/web/app-shared/components/bookmark-flag/bookmark-flag';
import CustomClickLink from '@bridebook/web/app-shared/components/custom-click-link/custom-click-link';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { ArticleTileSizes } from '@bridebook/web/app-shared/lib/articles/types';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import componentStyles from './article-tile.style';
import { Tag, Text, cn } from '@bridebook/aura';
import { useArticleCategoryName } from '@bridebook/web/app-shared/lib/articles/hooks/use-article-category-name';

interface IPropTypes {
  onToggleBookmark: () => void;
  isBookmarked: boolean;
  onLinkClick?: () => void;
  title: string;
  description?: string;
  categorySlug?: string;
  imageLink: string;
  imageWidth?: number;
  imageHeight?: number;
  size?: ArticleTileSizes;
  articlePath: string;
  tags?: { name: string; slug: string }[];
  firstArticle?: boolean;
  index?: number;
  date?: string;
}

const ArticleTile = ({
  onToggleBookmark,
  isBookmarked,
  onLinkClick,
  title,
  imageLink,
  imageWidth,
  imageHeight,
  size = 'big',
  articlePath,
  firstArticle,
  index,
  date,
  description,
  categorySlug,
}: IPropTypes) => {
  const { push } = useRouter();
  const market = useMarket();
  const { t } = useTranslation('articles');
  const smallSize = size === 'small';

  const onLinkClicked = useCallback(
    (e?: SyntheticEvent<any>): void => {
      if (e) e.preventDefault();
      if (onLinkClick) {
        onLinkClick();
      }
      if (smallSize) {
        push(`/${market.prefix}${articlePath}`);
      }
    },
    [market.prefix, onLinkClick, push, articlePath, smallSize],
  );

  const onBookmarkClicked = useCallback(
    (e: SyntheticEvent<any>): void => {
      e.preventDefault();
      if (onToggleBookmark) {
        onToggleBookmark();
      }
    },
    [onToggleBookmark],
  );

  const constructUrl = useMemo(() => {
    // Don't apply cloudinary transformations for regular absolute urls
    if ((imageLink || '').startsWith('https://')) return imageLink;
    const cloud = 'https://res.cloudinary.com/bridebook/image/upload';
    const options = `w_${imageWidth},h_${imageHeight},f_auto,dpr_${
      typeof window !== 'undefined' && window.devicePixelRatio > 1 ? 2 : 'auto'
    },c_fill,fl_progressive`;
    return decodeURI(`${cloud}/${options}/${imageLink}.jpg`);
  }, [imageHeight, imageLink, imageWidth]);

  const imageUrl = encodeURI(constructUrl);

  const styles = componentStyles(imageUrl, size, firstArticle, index);

  const articleCategory = useArticleCategoryName(categorySlug);

  return (
    <Box style={styles.wrapper} data-name="article-tile-wrap">
      <CustomClickLink
        href={`/${market.prefix}${articlePath}`}
        linkStyle={styles.articleLink}
        onCustomClick={onLinkClicked}
        underlined={smallSize}>
        <Box style={styles.articleTile}>
          <Box style={styles.imageWrap}>
            {!smallSize && (
              <>
                <Box style={styles.bookmarkWrapper}>
                  <BookmarkFlag {...{ onBookmarkClicked, isBookmarked, width: 30, height: 30 }} />
                </Box>
              </>
            )}
            <Link href={articlePath} passHref legacyBehavior>
              <Box style={styles.articleTileImageWrap}>
                <Box style={styles.articleTileImage} />
              </Box>
            </Link>
            {index === 0 && <Box style={styles.featuredPill}>{t('featured')}</Box>}
          </Box>
          <Box style={styles.titleCategoryWrap}>
            <Box>
              {firstArticle && articleCategory && (
                <Tag className="mb-3 hidden content-center lg:block" theme="indigoCrush10">
                  {articleCategory}
                </Tag>
              )}
              <Box as="h5" style={styles.title}>
                <Truncatable text={title} lineClamp={2} />
              </Box>
              {description && (
                <div className="mt-1 font-normal">
                  <Truncatable text={description} lineClamp={2} />
                </div>
              )}
            </Box>
            {date && (
              <Text
                className={cn(
                  'mt-1 text-xs',
                  firstArticle ? 'text-white lg:text-space-60' : 'text-space-60',
                )}>
                {date}
              </Text>
            )}
          </Box>
        </Box>
      </CustomClickLink>
    </Box>
  );
};

export default memo(ArticleTile);
